import React from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import ReactOnRails from "react-on-rails-pro/client";
import { loadableReady } from "@loadable/component";
import { pageComponents, utilityComponents } from "../javascripts/registeredComponents";

// Creates a renderer that defers hydration until all loadable chunks are ready.
// ReactOnRails detects renderers by: renderFunction=true + function.length===3.
// It delegates to the renderer instead of doing its own hydration.
// When SSR is disabled (empty container), falls back to createRoot for client rendering.
//
// Must return a promise -- v16's ClientSideRenderer awaits the renderer function,
// so a void return causes it to consider rendering complete before hydration finishes.
function createLoadableRenderer(Component) {
  const renderer = (props, _railsContext, domId) => {
    const doRender = () => {
      const domNode = document.getElementById(domId);
      if (domNode) {
        const element = React.createElement(Component, props);
        if (domNode.innerHTML) {
          hydrateRoot(domNode, element);
        } else {
          createRoot(domNode).render(element);
        }
      }
    };

    // When SSR is used, ChunkExtractor injects __LOADABLE_REQUIRED_CHUNKS__
    // to signal which chunks must load before hydration. Use loadableReady
    // to wait for them. When client-only rendering, render immediately.
    if (document.getElementById("__LOADABLE_REQUIRED_CHUNKS__")) {
      return new Promise((resolve) => {
        loadableReady(() => {
          doRender();
          resolve();
        });
      });
    } else {
      doRender();
      return Promise.resolve();
    }
  };
  renderer.renderFunction = true;
  return renderer;
}

const wrappedPages = Object.fromEntries(
  Object.entries(pageComponents).map(([name, Component]) => [
    name,
    createLoadableRenderer(Component),
  ]),
);

ReactOnRails.register({
  ...wrappedPages,
  ...utilityComponents,
});

